/**
 * Raw GCS stream → telemetry fields (ENG-271).
 *
 * `v1/input/aircraft/{sn}/gcs_telemetry` is the aircraft agent's MAVLink feed,
 * wrapped in the same `{header, payload: {_type, …}}` envelope the old
 * `flysafe/v1` feed used. The `v1/output` topics carry no satellites, HDOP or
 * GPS fix, and the output HDOP / GPS-lock were guesses from position accuracy.
 *
 * Deliberately NOT the legacy MAVLink demuxer: that one also writes position,
 * altitude, heading and AGL, which would take over from the output topics and
 * fight them (GLOBAL_POSITION_INT and TERRAIN_REPORT both write `agl`). This
 * reads only the fields below, and none of them is written by anything else.
 *
 * Not read on purpose: UTM_GLOBAL_POSITION.flight_state. It looked like a landed
 * state (3 while airborne in one capture) but reads 2 ("ground") for a whole
 * capture of an aircraft at 242 m doing 27 m/s, so it is not a reliable signal.
 *
 * The stream is heavy (~105 msg/s, ~91 kB/s per aircraft), so the source only
 * subscribes to it for the selected aircraft — see `setFocusedAircraft`.
 */
import type { TelemetryEnvelope } from '@/lib/mqtt/schemas/telemetry';
import { z } from 'zod';

/** `{header, payload: {_type, …}}` — the same envelope the old `flysafe/v1` feed used. */
const GcsEnvelope = z.object({
  header: z.object({ aircraft_serial: z.string() }),
  payload: z.object({ _type: z.string() }).passthrough(),
});

export interface GcsStreamUpdate {
  droneId: string;
  partial: Partial<TelemetryEnvelope>;
}

export const gcsStreamTopic = (serial: string): string =>
  `v1/input/aircraft/${serial}/gcs_telemetry`;

export const isGcsStreamTopic = (topic: string): boolean =>
  topic.startsWith('v1/input/aircraft/') && topic.endsWith('/gcs_telemetry');

const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const RAD_TO_DEG = 180 / Math.PI;
const round = (v: number, dp: number): number => Math.round(v * 10 ** dp) / 10 ** dp;

type Mapper = (p: Record<string, unknown>) => Partial<TelemetryEnvelope>;

const MAPPERS: Record<string, Mapper> = {
  // Satellites, HDOP (eph / 100) and GPS lock (fix type).
  GPS_RAW_INT: (p) => {
    const out: Partial<TelemetryEnvelope> = {};
    if (num(p.satellites_visible) && p.satellites_visible >= 0) out.gps_sats = p.satellites_visible;
    // `eph` is HDOP x 100; 65535 (UINT16_MAX) means unknown.
    if (num(p.eph) && p.eph < 65535) out.gps_hdop = p.eph / 100;
    if (num(p.fix_type)) {
      // GPS_FIX_TYPE: 0/1 no fix, 2 = 2D, 3 = 3D, 4 = DGPS, 5+ = RTK.
      out.gps_lock = p.fix_type >= 3 ? 'Acquired' : p.fix_type === 2 ? 'Acquiring' : 'Lost';
    }
    return out;
  },
  VFR_HUD: (p) => {
    const out: Partial<TelemetryEnvelope> = {};
    if (num(p.airspeed)) out.airspeed_mps = round(p.airspeed, 1);
    if (num(p.throttle) && p.throttle >= 0 && p.throttle <= 100) out.throttle_pct = p.throttle;
    return out;
  },
  ATTITUDE: (p) => {
    const out: Partial<TelemetryEnvelope> = {};
    if (num(p.roll)) out.roll_deg = round(p.roll * RAD_TO_DEG, 1);
    if (num(p.pitch)) out.pitch_deg = round(p.pitch * RAD_TO_DEG, 1);
    if (num(p.yaw)) out.yaw_deg = round((((p.yaw * RAD_TO_DEG) % 360) + 360) % 360, 0);
    return out;
  },
  // Terrain elevation under the aircraft and its height above it, for Ground
  // Clearance. Kept off `agl`/`aglM` (GLOBAL_POSITION_INT writes those too).
  TERRAIN_REPORT: (p) => {
    const out: Partial<TelemetryEnvelope> = {};
    if (num(p.terrain_height)) out.terrain_elev_m = round(p.terrain_height, 1);
    if (num(p.current_height)) out.terrain_clearance_m = round(p.current_height, 1);
    return out;
  },
  SCALED_PRESSURE: (p) => {
    const out: Partial<TelemetryEnvelope> = {};
    if (num(p.press_abs)) out.air_pressure_hpa = round(p.press_abs, 1);
    // `temperature` is centi-degrees Celsius.
    if (num(p.temperature)) out.air_temp_c = round(p.temperature / 100, 1);
    return out;
  },
};

/** The message types worth parsing; every other type is skipped unread. */
export const GCS_READ_TYPES: ReadonlySet<string> = new Set(Object.keys(MAPPERS));

/** Per-type minimum gap (ms). Each type arrives ~10 Hz but the UI flushes at 4 Hz, so
 *  parsing every copy is wasted work (~105 msg/s per aircraft otherwise). */
export const GCS_TYPE_MIN_GAP_MS = 200;

const TYPE_RE = /"_type"\s*:\s*"([A-Z0-9_]+)"/;

/** The MAVLink message type of a raw stream message, found without parsing the JSON. */
export function peekGcsType(text: string): string | null {
  return TYPE_RE.exec(text)?.[1] ?? null;
}

/** Every telemetry key this stream can write — what the source claims, and what
 *  it hands back to the output topics when the aircraft is deselected. */
export const GCS_STREAM_FIELDS: readonly (keyof TelemetryEnvelope)[] = [
  'gps_sats',
  'gps_hdop',
  'gps_lock',
  'airspeed_mps',
  'throttle_pct',
  'roll_deg',
  'pitch_deg',
  'yaw_deg',
  'air_pressure_hpa',
  'air_temp_c',
  'terrain_elev_m',
  'terrain_clearance_m',
];

/** One stream message → a partial telemetry update, or null for anything we don't read. */
export function gcsStreamToPartial(raw: unknown): GcsStreamUpdate | null {
  const parsed = GcsEnvelope.safeParse(raw);
  if (!parsed.success) return null;
  const { header, payload } = parsed.data;
  const mapper = MAPPERS[payload._type];
  if (!mapper) return null;
  const partial = mapper(payload as Record<string, unknown>);
  if (Object.keys(partial).length === 0) return null;
  return { droneId: header.aircraft_serial, partial };
}
